import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import './Auth.css';

function Auth({ onClose }) {
  const { authMode, signup, login, loginAsGuest, setAuthMode } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const closeAuthModal = () => {
    if (onClose) {
      onClose();
    }
  };

  // Login form states
  const [loginData, setLoginData] = useState({
    emailOrPhone: '',
    password: ''
  });

  // Signup form states
  const [signupData, setSignupData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: ''
  });

  // Guest form states
  const [guestData, setGuestData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: ''
  });

  // Handle login
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!loginData.emailOrPhone || !loginData.password) {
        setError('Please fill in all fields');
        setLoading(false);
        return;
      }

      login(loginData.emailOrPhone, loginData.password);
      setLoginData({ emailOrPhone: '', password: '' });
      closeAuthModal();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle signup
  const handleSignup = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!signupData.firstName || !signupData.lastName || !signupData.password) {
        setError('Please fill in all required fields');
        setLoading(false);
        return;
      }

      if (!signupData.email && !signupData.phone) {
        setError('Please provide either email or phone number');
        setLoading(false);
        return;
      }

      if (signupData.password !== signupData.confirmPassword) {
        setError('Passwords do not match');
        setLoading(false);
        return;
      }

      if (signupData.password.length < 6) {
        setError('Password must be at least 6 characters');
        setLoading(false);
        return;
      }

      signup(signupData);
      setSignupData({ firstName: '', lastName: '', email: '', phone: '', password: '', confirmPassword: '' });
      closeAuthModal();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle guest checkout
  const handleGuestCheckout = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      if (!guestData.firstName || !guestData.lastName) {
        setError('Please fill in your first and last name');
        setLoading(false);
        return;
      }

      if (!guestData.email && !guestData.phone) {
        setError('Please provide either email or phone number');
        setLoading(false);
        return;
      }

      loginAsGuest(guestData);
      setGuestData({ firstName: '', lastName: '', email: '', phone: '' });
      closeAuthModal();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-modal-overlay" onClick={closeAuthModal}>
      <div className="auth-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="auth-close-btn" onClick={closeAuthModal}>✕</button>

        {/* Login Mode */}
        {authMode === 'login' && (
          <div className="auth-section">
            <h2>👋 Welcome Back</h2>
            <p>Sign in to your account</p>

            {error && <div className="error-message">{error}</div>}

            <form onSubmit={handleLogin} className="auth-form">
              <div className="form-group">
                <label>Email or Phone Number</label>
                <input
                  type="text"
                  placeholder="your@email.com or +234 812 345 6789"
                  value={loginData.emailOrPhone}
                  onChange={(e) => setLoginData({ ...loginData, emailOrPhone: e.target.value })}
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Password</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    value={loginData.password}
                    onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
              </div>

              <button type="submit" className="auth-btn" disabled={loading}>
                {loading ? 'Signing In...' : 'Sign In'}
              </button>
            </form>

            <div className="auth-divider">or</div>

            <div className="auth-options">
              <button
                className="option-btn signup-btn"
                onClick={() => setAuthMode('signup')}
              >
                Create New Account
              </button>
              <button
                className="option-btn guest-btn"
                onClick={() => setAuthMode('guest')}
              >
                Continue as Guest
              </button>
            </div>
          </div>
        )}

        {/* Signup Mode */}
        {authMode === 'signup' && (
          <div className="auth-section">
            <h2>✨ Create Account</h2>
            <p>Join AfroStripes Fashion</p>

            {error && <div className="error-message">{error}</div>}

            <form onSubmit={handleSignup} className="auth-form">
              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    placeholder="John"
                    value={signupData.firstName}
                    onChange={(e) => setSignupData({ ...signupData, firstName: e.target.value })}
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    placeholder="Doe"
                    value={signupData.lastName}
                    onChange={(e) => setSignupData({ ...signupData, lastName: e.target.value })}
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={signupData.email}
                  onChange={(e) => setSignupData({ ...signupData, email: e.target.value })}
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  placeholder="+234 812 345 6789"
                  value={signupData.phone}
                  onChange={(e) => setSignupData({ ...signupData, phone: e.target.value })}
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Password *</label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Min. 6 characters"
                    value={signupData.password}
                    onChange={(e) => setSignupData({ ...signupData, password: e.target.value })}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    className="toggle-password"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label>Confirm Password *</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Confirm password"
                  value={signupData.confirmPassword}
                  onChange={(e) => setSignupData({ ...signupData, confirmPassword: e.target.value })}
                  disabled={loading}
                />
              </div>

              <button type="submit" className="auth-btn" disabled={loading}>
                {loading ? 'Creating Account...' : 'Create Account'}
              </button>
            </form>

            <div className="auth-divider">Have an account?</div>

            <button
              className="option-btn login-btn"
              onClick={() => setAuthMode('login')}
            >
              Sign In Instead
            </button>
          </div>
        )}

        {/* Guest Mode */}
        {authMode === 'guest' && (
          <div className="auth-section">
            <h2>🛍️ Guest Checkout</h2>
            <p>No account needed - shop now, account later</p>

            {error && <div className="error-message">{error}</div>}

            <form onSubmit={handleGuestCheckout} className="auth-form">
              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    placeholder="John"
                    value={guestData.firstName}
                    onChange={(e) => setGuestData({ ...guestData, firstName: e.target.value })}
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    placeholder="Doe"
                    value={guestData.lastName}
                    onChange={(e) => setGuestData({ ...guestData, lastName: e.target.value })}
                    disabled={loading}
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Email</label>
                <input
                  type="email"
                  placeholder="your@email.com"
                  value={guestData.email}
                  onChange={(e) => setGuestData({ ...guestData, email: e.target.value })}
                  disabled={loading}
                />
              </div>

              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  placeholder="+234 812 345 6789"
                  value={guestData.phone}
                  onChange={(e) => setGuestData({ ...guestData, phone: e.target.value })}
                  disabled={loading}
                />
              </div>

              <button type="submit" className="auth-btn guest-checkout-btn" disabled={loading}>
                {loading ? 'Processing...' : 'Continue as Guest'}
              </button>
            </form>

            <div className="auth-divider">Want an account?</div>

            <div className="auth-options">
              <button
                className="option-btn login-btn"
                onClick={() => setAuthMode('login')}
              >
                Sign In
              </button>
              <button
                className="option-btn signup-btn"
                onClick={() => setAuthMode('signup')}
              >
                Create Account
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Auth;
